/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable react/prop-types */
import React from 'react';
import Style from './index.less';
import Menu from '@tencent/dui/lib/components/Menu';
import { IItemProps } from '@tencent/dui/lib/components/Menu/interface';
import Divider from '@tencent/dui/lib/components/Divider';
import Tooltip, { ITooltipProps } from './tooltip';
import OptionMenuItem from './option-menu-item';
import classNames from 'classnames';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import PropTypes from 'prop-types';

const menuStyle: React.CSSProperties = {
    margin: '-8px 0',
    border: 0,
    boxShadow: 'none',
    backgroundColor: 'transparent',
    backdropFilter: 'none',
    width: '200px',
};

export interface MenuItem extends IItemProps {
    /** 追加 id 后缀 */
    aid?: string;
    toggle?: boolean;

    /** 历史 typo, IItemProps['disable'] */
    disable?: boolean;
    /** 等同于 IItemProps['title']  */
    content?: string;

    children?: MenuItem[];

    /** hover 状态下展示 tooltip */
    tooltip?: ITooltipProps & {
        /** 用以渲染 ITooltipProps['title'], 且优先级更高 */
        titleComponent: React.FunctionComponent | React.ComponentClass;
        /** 传给 `titleComponent` 的 props */
        titleProps: Record<string, unknown>;
    };
}

export interface OptionMenuProps {
    onMenuClick: (id: string) => void;
    menuData?: MenuItem[];
    data: any;
    style: any;
}

interface OptionMenuState {
    menuList: OptionMenuProps['menuData'];
}

/**
 * 通用菜单
 * 可以配合dropDown一起使用。
 * 支持多级菜单和自定义item面板。
 */
export default class OptionMenu<
    P extends OptionMenuProps = OptionMenuProps
> extends React.Component<P, OptionMenuState> {
    private timer: number | null = null;
    private renderCustomElement = false;

    public componentDidMount() {
        if (this.isAsyncLoadCustom()) {
            this.timer = window.setTimeout(() => {
                this.renderCustomElement = true;
                this.forceUpdate();
            }, 250);
        }
    }

    public componentWillUnmount() {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }

    public render() {
        return (
            <Menu
                onClick={this.menuItemClick}
                selectable={false}
                style={{ ...menuStyle, ...this.props.style }}
            >
                {this.renderMenuItem(this.props.menuData || this.props.data || [])}
            </Menu>
        );
    }

    /** 子类继承实现，是否异步加载自定义面板，这会优化弹出速度 */
    protected isAsyncLoadCustom = () => false;

    /**
     * 子类继承实现，实现自定义面板
     * @param id: 即 `ActionType`
     */
    protected getCustomElement = (): JSX.Element | null => null;

    private menuItemClick = (id: string) => {
        this.props.onMenuClick?.(id);
    };

    private renderMenuItem = (menuItemList: MenuItem[]) => {
        const itemList = menuItemList.map((item, index) => {
            const key = item.aid ? `${item.id}&${item.aid}` : item.id;
            const { disable, toggle = true, tooltip: tooltipProps } = item; // 是否不可用

            // 功能开关，用于控制是否显示该元素
            if (!toggle) return null;

            if (key === 'divider-menu-item') return <Divider key={index} />;

            if (item.children) {
                return (
                    <Menu.SubMenu
                        id={key}
                        key={key}
                        disabled={disable}
                        popupStyle={{ overflow: 'hidden', width: 200 }}
                        icon={item.icon}
                        title={<OptionMenuItem {...item}></OptionMenuItem>}
                        selectable={item.id === ActionType.SLIDE_SIZE}
                    >
                        {this.renderMenuItem(item.children)}
                    </Menu.SubMenu>
                );
            }

            const customElement = (this.isAsyncLoadCustom() && !this.renderCustomElement)
                ? null
                : this.getCustomElement();

            if (customElement) {
                const itemClass = classNames({
                    [Style['option-menu-item-custom']]: true,
                    [Style.disable]: true,
                });

                return (
                    <Menu.Item id={key} key={key} className={itemClass}>
                        {customElement}
                    </Menu.Item>
                );
            }

            const menuItem = (
                <Menu.Item
                    id={key}
                    key={key}
                    disabled={disable}
                    icon={item.icon}
                >
                    <OptionMenuItem {...item} disable={disable}></OptionMenuItem>
                </Menu.Item>
            );

            if (!tooltipProps) return menuItem;

            const { titleComponent, titleProps, title, ...restProps } = tooltipProps;
            const tooltipTitle = titleComponent ? React.createElement(titleComponent, titleProps) : title;

            return (
                <Tooltip
                    key={key}
                    title={tooltipTitle}
                    placement={['right', 'left']}
                    alignment="start"
                    style={{ width: '200px' }}
                    hoverDelay={0}  // 消失延迟建议改为0，否则和子菜单不一致
                    {...restProps}
                >
                    {menuItem}
                </Tooltip>
            );
        });
        return itemList;
    };
}
